<template>
    <div class="cz2__modal-popup is-kiosk cz2__service-menu">
        <div class="cz2__modal-popup__body">
            <focus-lock>
                <button class="cz2__modal-popup__close" :aria-label="$t('ACTION_CLOSE')" @click.prevent="close" />

                <div class="cz2__modal-popup__header">
                    {{$t('SERVICE_MENU')}} {{configuration.version}}
                </div>

                <div v-if="!pinEntered && !reprintPinEntered" class="cz2__service-menu__pin">
                    <div class="cz2__modal-popup__copy cz2__text--center">
                        {{$t('SERVICE_MENU_PIN')}}
                    </div>

                    <div class="cz2__service-menu__pin-row">
                        <btn primary narrow auto @click="addPin('1')">1</btn>
                        <btn primary narrow auto @click="addPin('2')">2</btn>
                        <btn primary narrow auto @click="addPin('3')">3</btn>
                    </div>

                    <div class="cz2__service-menu__pin-row">
                        <btn primary narrow auto @click="addPin('4')">4</btn>
                        <btn primary narrow auto @click="addPin('5')">5</btn>
                        <btn primary narrow auto @click="addPin('6')">6</btn>
                    </div>

                    <div class="cz2__service-menu__pin-row">
                        <btn primary narrow auto @click="addPin('7')">7</btn>
                        <btn primary narrow auto @click="addPin('8')">8</btn>
                        <btn primary narrow auto @click="addPin('9')">9</btn>
                    </div>

                    <div class="cz2__service-menu__pin-row">
                        <btn primary narrow @click="addPin('0')">0</btn>
                    </div>
                </div>

                <div v-if="reprintPinEntered" class="cz2__service-menu__menu">
                    <template v-if="savedTickets">
                        <div v-for="t in [...savedTickets].reverse()" :key="t.id" class="cz2__service-menu__menu-row">
                            <btn primary @click="reprint(t)">
                                <span class="cz2__ticket-name">{{ $t('REPRINT') }} {{getName(t)}} &mdash; {{getPhone(t)}}</span>
                                <span class="cz2__ticket-date">{{formatTime(t.when)}}</span>
                            </btn>
                        </div>
                    </template>

                    <div v-if="lastKnownError" class="cz2__service-menu__menu-row">
                        <div class="cz2__service-menu__label">
                            {{$t('LAST_ERROR')}}
                        </div>
                        <div class="cz2__service-menu__value">
                            <span v-if="lastKnownError.message">
                                {{lastKnownError.message}}
                            </span>
                            <span v-else-if="lastKnownError.paper === false">
                                {{$t('OUT_OF_PAPER')}}
                            </span>
                            <span v-else>
                                {{lastKnownError}}
                            </span>
                        </div>
                    </div>
                    <div class="cz2__service-menu__menu-row">
                        <div class="cz2__service-menu__label">
                            {{$t('APP_STATE')}}
                        </div>
                        <div class="cz2__service-menu__value">
                            {{$t('APP_STATE_PRINTING')}}:
                            <span v-if="backgroundState && backgroundState.printing">
                                {{backgroundState.printing}}
                            </span>
                            <span v-else>
                                {{$t('APP_STATE_IDLE')}}
                            </span>
                        </div>
                        <div class="cz2__service-menu__value">
                            {{$t('APP_STATE_UPDATING')}}:
                            <span v-if="backgroundState && backgroundState.updating">
                                {{backgroundState.updating}}
                            </span>
                            <span v-else>
                                {{$t('APP_STATE_IDLE')}}
                            </span>
                        </div>
                    </div>

                    <div class="cz2__service-menu__menu-row">
                        <div class="cz2__service-menu__label">
                            {{$t('UP_SINCE')}}
                        </div>
                        <div class="cz2__service-menu__value">
                            <span>{{formatTime($store.state.uptime)}}</span>
                        </div>
                    </div>
                </div>

                <div v-if="pinEntered" class="cz2__service-menu__menu">
                    <div class="cz2__service-menu__menu-row">
                        <btn primary narrow @click="checkUpdates">{{ $t('CHECK_FOR_UPDATES') }}</btn>
                    </div>
                    <div class="cz2__service-menu__menu-row">
                        <btn primary narrow @click="exitApp">{{ $t('EXIT_APP') }}</btn>
                    </div>
                    <div class="cz2__service-menu__menu-row">
                        <btn primary narrow @click="restartApp">{{ $t('RESTART_APP') }}</btn>
                    </div>
                    <div class="cz2__service-menu__menu-row">
                        <btn primary narrow :error="recipeOverride" @click="toggleRecipeOverride">
                            <span class="cz2__ticket-name">{{ $t('INVENTORY_BYPASS') }}</span>
                            <span class="cz2__ticket-date cz2__ml-10">{{recipeOverride ? $t('ONE_TIME_ENABLED') : $t('DISABLED')}}</span>
                        </btn>
                    </div>
                    <div class="cz2__service-menu__menu-row">
                        <btn primary narrow @click="testPrinter">{{ $t('PRINTER_TEST') }}</btn>
                    </div>
                    <div v-if="lastKnownError" class="cz2__service-menu__menu-row">
                        <div class="cz2__service-menu__label">
                            {{$t('LAST_ERROR')}}
                        </div>
                        <div class="cz2__service-menu__value">
                            <span v-if="lastKnownError.message">
                                {{lastKnownError.message}}
                            </span>
                            <span v-else-if="lastKnownError.paper === false">
                                {{$t('OUT_OF_PAPER')}}
                            </span>
                            <span v-else>
                                {{lastKnownError}}
                            </span>
                        </div>
                    </div>
                    <div class="cz2__service-menu__menu-row">
                        <div class="cz2__service-menu__label">
                            {{$t('APP_STATE')}}
                        </div>
                        <div class="cz2__service-menu__value">
                            {{$t('APP_STATE_PRINTING')}}:
                            <span v-if="backgroundState && backgroundState.printing">
                                {{backgroundState.printing}}
                            </span>
                            <span v-else>
                                {{$t('APP_STATE_IDLE')}}
                            </span>
                        </div>
                        <div class="cz2__service-menu__value">
                            {{$t('APP_STATE_UPDATING')}}:
                            <span v-if="backgroundState && backgroundState.updating">
                                {{backgroundState.updating}}
                            </span>
                            <span v-else>
                                {{$t('APP_STATE_IDLE')}}
                            </span>
                        </div>
                    </div>

                    <div class="cz2__service-menu__menu-row">
                        <div class="cz2__service-menu__label">
                            {{$t('UP_SINCE')}}
                        </div>
                        <div class="cz2__service-menu__value">
                            <span>{{formatTime($store.state.uptime)}}</span>
                        </div>
                    </div>
                </div>
            </focus-lock>
        </div>
    </div>
</template>

<style lang="scss">
    @import '../style/variables';

    #cz2.cz2 {
        .cz2__service-menu {
            .cz2__btn {
                font-size: 16px;

                &:hover {
                    font-size: 19px;
                    box-shadow: 0px 4px 2px 1px rgba(0, 0, 0, 0.2) !important;
                }

                &.cz2__btn--narrow {
                    font-size: 16px;

                    &:hover {
                        font-size: 19px;
                    }
                }
            }
        }

        .cz2__service-menu__pin {
            .cz2__modal-popup__copy {
                margin-bottom: 25px;
            }
        }

        .cz2__ticket-name {
            white-space: normal;
        }

        .cz2__ticket-date {
            display: block;

            font-size: 16px;
        }

        .cz2__service-menu__pin-row {
            display: flex;

            justify-content: center;

            & + .cz2__service-menu__pin-row {
                margin-top: 25px;
            }

            button {
                width: 64px;
                height: 64px;

                margin-left: 8px;
                margin-right: 8px;
            }
        }

        .cz2__service-menu__label {
            @include font(h3);
            color: color(primaryBlue);
        }

        .cz2__service-menu__value {
            @include font(bodyRegular);
            color: #000;

            span {
                font-weight: 700;
            }
        }

        .cz2__service-menu__menu-row {
            text-align: center;

            & + .cz2__service-menu__menu-row {
                margin-top: 25px;
            }
        }
    }
</style>

<script>
    import moment from 'moment';
    import FocusLock from 'vue-focus-lock';

    import Btn from './ui/Btn.vue';

    export default {
        components: {
            FocusLock,
            Btn,
        },
        data() {
            return {
                lastChanceMinutesLeft: null,
                lastChanceSecondsLeft: null,

                pin: '',
            };
        },

        computed: {
            /**
             * Current configuration.
             */
            configuration() {
                if (this.$store.state.customizer.options == null) {
                    return {};
                }

                return this.$store.state.customizer.options.configuration || {};
            },

            backgroundState() {
                return this.$store.state.backgroundState;
            },

            pinEntered() {
                const required = this.configuration.serviceMenuPin;

                return this.pin.includes(required);
            },

            reprintPinEntered() {
                const required = this.configuration.reprintMenuPin;

                return this.pin.includes(required);
            },

            lastKnownError() {
                let error = this.$store.state.lastKnownError;

                if (error && error.response && error.response.data && error.response.data.modelState) {
                    const errors = [];

                    Object.keys(error.response.data.modelState).forEach((k) => {
                        errors.push(error.response.data.modelState[k]);
                    });

                    error = errors.join(',');
                }

                return error;
            },

            savedTickets() {
                return this.$store.state.customizer.savedTickets;
            },

            recipeOverride() {
                return this.$store.state.customizer.recipeOverride;
            },
        },

        mounted() {
            this.$store.dispatch('log', {
                level: 'info',
                message: 'Service menu shown',
            });

            this.resetTimer();
        },

        beforeDestroy() {
            if (this.timer) {
                clearTimeout(this.timer);
            }
        },

        methods: {
            close() {
                this.$store.dispatch('log', {
                    level: 'info',
                    message: 'Service menu was closed',
                });

                this.$store.dispatch('showServiceMenu', false);
            },

            addPin(v) {
                this.resetTimer();

                this.$store.dispatch('log', {
                    level: 'info',
                    message: `Service menu pin key ${v} pressed`,
                });

                this.pin += v;
            },

            exitApp() {
                this.resetTimer();

                if (this.$store.state.options && this.$store.state.options.onExitApp) {
                    this.$store.state.options.onExitApp();
                }
            },

            checkUpdates() {
                this.resetTimer();

                if (this.$store.state.options && this.$store.state.options.onCheckUpdates) {
                    this.$store.state.options.onCheckUpdates();
                }
            },

            restartApp() {
                this.resetTimer();

                if (this.$store.state.options && this.$store.state.options.onRestartApp) {
                    this.$store.state.options.onRestartApp();
                }
            },

            testPrinter() {
                this.resetTimer();

                if (this.$store.state.options && this.$store.state.options.onTestPrinter) {
                    this.$store.state.options.onTestPrinter().then(() => {
                        this.$store.dispatch('setLastError', null);
                    }).catch((error) => {
                        this.$store.dispatch('setLastError', error);
                    });
                }
            },

            resetTimer() {
                if (this.timer) {
                    clearTimeout(this.timer);
                }

                this.timer = setTimeout(() => {
                    this.timeoutExpired();
                }, 60000);
            },

            timeoutExpired() {
                this.close();
            },

            getName(t) {
                let name = t.contactName;

                if (name) {
                    const parts = name.split(' ').filter((x) => x.length > 0);

                    if (parts.length > 1) {
                        name = `${parts[0]} ${parts[1].substr(0, 1)}.`;
                    }
                }

                return name;
            },

            getPhone(t) {
                let phone = t.contactPhone;

                if (phone && phone.length > 4) {
                    phone = `...-${phone.substr(phone.length - 4, 4)}`;
                }

                return phone;
            },

            reprint(t) {
                this.$store.dispatch('log', {
                    level: 'info',
                    message: `Reprint old ticket ${JSON.stringify(t, null, '    ')}`,
                });

                this.$store.dispatch('log', {
                    level: 'info',
                    message: 'Sending to host',
                });

                if (this.$store.state.options.onPrint) {
                    this.$store.dispatch('showPrintingProgress', true);

                    this.$store.state.options.onPrint(t).then(() => {
                        this.$store.dispatch('log', {
                            level: 'info',
                            message: 'Reprinting successful',
                        });

                        this.$store.dispatch('showPrintingProgress', false);
                    }).catch((error) => {
                        this.$store.dispatch('log', {
                            level: 'info',
                            message: `Reprinting failed: ${error}`,
                        });

                        this.$store.dispatch('showPrintingProgress', false);

                        this.$store.dispatch('showPrintingError', error);
                    });
                }
            },

            formatTime(t) {
                return moment(t).format('lll');
            },

            toggleRecipeOverride() {
                this.$store.dispatch('setRecipeOverride', !this.recipeOverride);
            },
        },
    };
</script>
